import React, {createRef, useState, useContext} from 'react';
import {makeStyles} from "@material-ui/core/styles";
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import {TablePagination, Checkbox, Toolbar, Select, MenuItem, useTheme} from "@material-ui/core";
import ChromeTabs from "../../../controls/ChromeTabs";

import SystemContext from "../../../SystemContext";
import CompaniesStyles from "../styles/CompaniesStyles";
import CompanyMessagingList from "./CompanyMessagingList";
import CurrencySelector from "../../Currencies/components/CurrencySelector";
import ExchangeRateProviderSelector from "../../Currencies/components/ExchangeRateProviderSelector";
import CompanyContactList from "./CompanyContactList";

import { useCompanies, useAddCompany, useUpdateCompany, useDeleteCompany } from "../../../services/companies";

import CompaniesValidator from "../validators/CompaniesValidator";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import { 
  MaskedText, CheckboxEditor,
} from "../../../controls";
import { defaultComponents } from '../../../controls/Material-Table-Components';

const CompanyList = (_props) => {
  const classes = makeStyles(CompaniesStyles)();
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(CompaniesValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    currentCompanyUuid: null,
    currentCompanyName: null,
    selectedCompanyIndex: undefined,
  });

  const [companyTabs, setCompanyTabs] = useState(
    [
      {label: "Contacts", value: "contacts"},
      {label: "Messaging", value: "messaging"},
    ]
  );
  const [currentTab, setCurrentTab] = useState("contacts");

  const handleTabChange = (e, i) => {
    setCurrentTab(i);
  }

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddCompany = useAddCompany();
  const handleUpdateCompany = useUpdateCompany();
  const handleDeleteCompany = useDeleteCompany();

  const queryClient = useQueryClient();
  const extraKeys = [];
  const companies = useCompanies({tableRef, queryClient, extraKeys});

  const contractTypes = SystemData.contractTypes;

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        title: "Company Name",
        field: "companyName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...ColumnWidth(100),
        ...baseColumn,
        title: "Company Code",
        field: "companyCode",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(20)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...ColumnWidth(100),
        ...baseColumn,
        title: "Branch Code",
        field: "branchCode",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(20)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...ColumnWidth(10),
        ...baseColumn,
      },
      {
        ...(ColumnWidth(90)),
        title: "Client",
        field: "isClient",
        editable: state.canEdit ? "always" : "never",
        emptyValue: false,
        initialEditValue: false,
        //type: "boolean",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            onChange={ (checked) => {
              let rowData = { ...props.rowData };
              if (checked)
                rowData = {...rowData, isClient: true, isSupplier: false, isPermitSupplier: false, isAirline: false, isShipper: false, isConsignee: false};
              props.onRowDataChange(rowData);
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.isClient === true} value="true" />
      },
      {
        ...(ColumnWidth(90)),
        title: "Supplier",
        field: "isSupplier",
        editable: state.canEdit ? "always" : "never",
        emptyValue: false,
        initialEditValue: false,
        //type: "boolean",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            onChange={ (checked) => {
              let rowData = { ...props.rowData };
              if (checked)
                rowData = {...rowData, isSupplier: true, isClient: false, isPermitSupplier: false, isAirline: false, isShipper: false, isConsignee: false};
              props.onRowDataChange(rowData);
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.isSupplier === true} value="true" />
      },
      {
        ...(ColumnWidth(90)),
        title: "Permit Supplier",
        field: "isPermitSupplier",
        editable: state.canEdit ? "always" : "never",
        emptyValue: false,
        initialEditValue: false,
        //type: "boolean",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            onChange={ (checked) => {
              let rowData = { ...props.rowData };
              if (checked)
                rowData = {...rowData, isPermitSupplier: true, isClient: false, isSupplier: false, isAirline: false, isShipper: false, isConsignee: false};
              props.onRowDataChange(rowData);
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.isPermitSupplier === true} value="true" />
      },
      {
        ...(ColumnWidth(90)),
        title: "Airline",
        field: "isAirline",
        editable: state.canEdit ? "always" : "never",
        emptyValue: false,
        initialEditValue: false,
        //type: "boolean",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            onChange={ (checked) => {
              let rowData = { ...props.rowData };
              if (checked)
                rowData = {...rowData, isAirline: true, isClient: false, isSupplier: false, isPermitSupplier: false, isShipper: false, isConsignee: false};
              props.onRowDataChange(rowData);
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.isAirline === true} value="true" />
      },
      {
        ...(ColumnWidth(90)),
        title: "Shipper",
        field: "isShipper",
        editable: state.canEdit ? "always" : "never",
        emptyValue: false,
        initialEditValue: false,
        //type: "boolean",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            onChange={ (checked) => {
              let rowData = { ...props.rowData };
              if (checked)
                rowData = {...rowData, isShipper: true, isClient: false, isSupplier: false, isPermitSupplier: false, isAirline: false, isConsignee: false};
              props.onRowDataChange(rowData);
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.isShipper === true} value="true" />
      },
      {
        ...(ColumnWidth(90)),
        title: "Consignee",
        field: "isConsignee",
        editable: state.canEdit ? "always" : "never",
        emptyValue: false,
        initialEditValue: false,
        //type: "boolean",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            onChange={ (checked) => {
              let rowData = { ...props.rowData };
              if (checked)
                rowData = {...rowData, isConsignee: true, isClient: false, isSupplier: false, isPermitSupplier: false, isAirline: false, isShipper: false};
              props.onRowDataChange(rowData);
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.isConsignee === true} value="true" />
      },
      {
        ...ColumnWidth(100),
        ...baseColumn,
        title: "MCS Code",
        field: "mcsCode",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(20)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...ColumnWidth(100),
        ...baseColumn,
        title: "Contract Type",
        field: "contractType",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: contractTypes[0],
        editComponent: (props) => (
          <Select
            labelId="Contract Type"
            id="Contract Type"
            value={props.value}
            onChange={e => {
              props.onChange(e.target.value);
            }}
          >
            {contractTypes.map((item) => {
              return (<MenuItem key={item} value={item}>{item}</MenuItem>);
            })
            }
          </Select>
        ),
        render: (rowData) => (<div>{rowData.contractType}</div>),
      },
      {
        ...ColumnWidth(140),
        ...baseColumn,
        title: "Exchange Rate Provider",
        field: "exchangeRateType",
        editComponent: (props) => (
          <ExchangeRateProviderSelector
            type="SYSTEM"
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...ColumnWidth(100),
        ...baseColumn,
        title: "Default Currency",
        field: "defaultCurrencyCode",
        editComponent: (props) => (
          <CurrencySelector
            queryClient= { props.queryClient }
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
    ];
  };

  const handleRowClick = (event, rowData) => {
    setState(
    {
      ...state,
      companyUuid: rowData.companyUuid,
      companyName: rowData.companyName,
      selectedCompanyIndex: rowData.tableData.id
    });

    //rowData.tableData.checked = true;
  };

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20 }}>
        <MaterialTable
          onRowClick={handleRowClick}
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            //selection: true,
            //rowStyle: rowData => ({ backgroundColor: rowData.tableData.checked ? '#37b15933' : '' }),
            rowStyle: rowData => ({ backgroundColor: state.selectedCompanyIndex !== undefined && rowData.tableData.id === state.selectedCompanyIndex ? '#F69259' : '' }),
            addRowPosition: "first",
            actionsColumnIndex: -1,
            cellStyle: { padding: "8px 2px", },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            toolbarButtonAlignment: "right",
            //paging: false,
            pageSize: 5,
            detailPanelType: "single",
            //showTitle: false
            padding: "dense",
          }}
          components={{
            ...defaultComponents,
            Pagination: (props) => (
              <TablePagination
                {...props}
                rowsPerPageOptions={[5, 10, 15]}
              />
            ),
          }}
          title="Company Maintenance"
          columns={getColumns({canEdit: state.canEdit})}
          data={companies.data}
          editable={{
            isEditable: () => {
              return state.canEdit;
            },
            isDeletable: () => {
              return state.canEdit;
            },
            onRowAdd: state.canEdit
              ? (newData) => new Promise((resolve, reject) => {
                handleAddCompany.mutate({record: newData, resolve, reject, extraKeys});
              })
              : undefined,
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                handleUpdateCompany.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
              }),
            onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteCompany.mutate({oldRecord: oldData, resolve, reject, extraKeys});
              }),
          }}
        />

        <div style={{ height: 10 }}></div>

        <Toolbar >
          <ChromeTabs
            id={"CompanyDetails"}
            //style={{ alignSelf: "flex-end", display: "inline-block", align: "left" }}
            style={{ marginTop: 10 }}
            tabs={companyTabs}
            tabStyle={{
              bgColor: "#E6E8EB",
              selectedBgColor: "#ffffff",
              color: "rgba(0,0,0,0.87)",
            }}
            tabProps={{
              disableRipple: false,
            }}
            value={currentTab}
            onChange={handleTabChange}
          />
        </Toolbar>
        <div style={{width: "100%"}}>
          {currentTab === "contacts"
            ?
              <div className={classes.rooted} >
                <CompanyContactList companyUuid={state.companyUuid} companyName={state.companyName} canEdit={state.canEdit}/>
              </div>
            :
              <div className={classes.rooted} >
                <CompanyMessagingList companyUuid={state.companyUuid} companyName={state.companyName} canEdit={state.canEdit}/>
              </div>
          }
        </div>
      </div>
  );

}

export default CompanyList;
